import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  signal,
} from '@angular/core';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { GridApi, ICellRendererParams } from 'ag-grid-community';
import {
  catchError,
  finalize,
  from,
  isObservable,
  Observable,
  tap,
  throwError,
} from 'rxjs';

export type DecisionStatus = 'approved' | 'declined' | 'pending';
export interface DecisionActions {
  approve: (rowId: string) => void | Promise<void> | Observable<void>;
  decline: (rowId: string) => void | Promise<void> | Observable<void>;
}

type Params = ICellRendererParams & {
  status?: DecisionStatus;
  actions?: DecisionActions;
};

@Component({
  selector: 'app-decision-cell-renderer',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './decision-cell-renderer.component.html',
  styleUrl: './decision-cell-renderer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecisionCellRendererComponent implements ICellRendererAngularComp {
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private readonly statusSignal = signal<DecisionStatus>('pending');
  readonly isLoading = signal(false);
  readonly canAct = computed(() => !this.isLoading() && !!this.rowId);

  private actions?: DecisionActions;
  private rowId: string | null = null;
  private gridApi?: GridApi;

  @Input() set status(value: DecisionStatus) {
    this.statusSignal.set(value ?? 'pending');
  }
  get status(): DecisionStatus {
    return this.statusSignal();
  }

  agInit(params: Params): void {
    this.statusSignal.set(params.status ?? params.data?.docStatus ?? 'pending');
    this.actions = params.actions ?? this.actions;
    this.rowId = params.data?.id ?? null;
    this.gridApi = params.api;
  }

  refresh(): boolean {
    return false;
  }

  onApprove(): void {
    this.apply('approve', this.actions?.approve);
  }
  onDecline(): void {
    this.apply('decline', this.actions?.decline);
  }

  private apply(
    kind: 'approve' | 'decline',
    fn?: (rowId: string) => void | Promise<void> | Observable<void>
  ): void {
    if (!fn || !this.rowId || this.isLoading()) return;

    const out = fn(this.rowId);
    const obs: Observable<void> = isObservable(out)
      ? (out as Observable<void>)
      : from(Promise.resolve(out as void));

    this.isLoading.set(true);
    obs
      .pipe(
        tap(() => {
          this.toast.success(
            this.translate.instant(
              kind === 'approve'
                ? 'admin.toasts.approvedSuccess'
                : 'admin.toasts.declinedSuccess'
            )
          );
          this.refreshGridData();
        }),
        catchError((err) => {
          this.toast.error(
            this.translate.instant(
              kind === 'approve'
                ? 'admin.toasts.approvedFailed'
                : 'admin.toasts.declinedFailed'
            )
          );
          return throwError(() => err);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  private refreshGridData(): void {
    const api = this.gridApi;
    if (!api) return;

    const anyApi = api as any;
    const isInfinite =
      typeof anyApi.refreshInfiniteCache === 'function' ||
      typeof anyApi.purgeInfiniteCache === 'function';

    if (isInfinite) {
      anyApi.purgeInfiniteCache?.() ?? anyApi.refreshInfiniteCache?.();
      return;
    }

    api.refreshClientSideRowModel?.('everything');
    api.refreshCells?.({ force: true });
    api.redrawRows?.();
  }
}
